import React, { useState, useEffect } from "react";
import {
  Grid, Typography, Box, Tabs, Tab, TextField, InputAdornment, IconButton
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Search } from "@mui/icons-material";
import { useMsal } from "@azure/msal-react";
import apiCall from "../utils/apiCall";
import { GET_PROVISION_DETAILS, UPDATE_REQUEST } from "../endpoint";
import LoadingDashboard from "./LoadingDashboard";
import nopending_img from "../assets/nopending.svg";
import { useAppDispatch } from "../hooks/reduxhook";
import { addError } from "../redux/slices/errorSlice";
import { pendingSteps, fullfilledSteps } from "./AdminTourSteps";
import ProvisionRow from "./ProvisionRow";
import { APPROVED } from "../data/constants";

interface I_Provision {
  request: string;
  type: string;
  user_id: string;
  user_name: string;
  user_email: string;
  requestTime?: string; 
}

interface ProvisionProps {
  setProvisionSteps: (steps: any[]) => void;
}

const Provision: React.FC<ProvisionProps> = ({ setProvisionSteps }) => {
  const { accounts } = useMsal();
  const account = accounts[0];
  const dispatch = useAppDispatch();

  const [approvedUsers, setApprovedUsers] = useState<I_Provision[]>([]);
  const [pendingUsers, setPendingUsers] = useState<I_Provision[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    setLoading(true);
    apiCall("POST", GET_PROVISION_DETAILS, { user_email: account.username })
      .then((res) => {
        const addRequestTime = (users: I_Provision[]) => users.map(user => ({
          ...user,
          requestTime: `${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`
        }));

        const approvedWithTime = addRequestTime(res.approved);
        const pendingWithTime = addRequestTime(res.pending);

        setApprovedUsers(approvedWithTime);
        setPendingUsers(pendingWithTime);
      })
      .catch(() => dispatch(addError("Failed to load the details of the user. Please try again.")))
      .finally(() => setLoading(false));
  }, [account.username, dispatch]);

  useEffect(() => {
    setProvisionSteps(tabValue === 0 ? pendingSteps : fullfilledSteps);
  }, [tabValue, setProvisionSteps]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  const updateUserRole = (userId: string, userEmail: string, newRole: string): Promise<void> => {
    return apiCall("POST", UPDATE_REQUEST, {
      req_email: userEmail,
      user_type: newRole === "remove" ? "none" : newRole,
      user_request: newRole === "remove" ? "none" : APPROVED,
    })
      .then((res) => {
        setApprovedUsers(res.result?.approved || []);
        setPendingUsers(res.result?.pending || []);
      })
      .catch((error) => {
        dispatch(addError("Failed to update the user role. Please try again."));
        throw error;
      });
  };

  const columns: GridColDef[] = [
    { field: 'user_name', headerName: 'Name', flex: 1, minWidth: 150 },
    { field: 'user_email', headerName: 'Email', flex: 1.5, minWidth: 200 },
    {
      field: 'role',
      headerName: 'Role',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <ProvisionRow
          item={params.row}
          updateUserRole={updateUserRole}
        />
      )
    }
  ];

  const getUserList = () => tabValue === 0 ? pendingUsers : approvedUsers;

  const rows = getUserList()
    .filter(user => user.user_email.toLowerCase().includes(searchQuery.toLowerCase()))
    .map(user => ({ ...user, id: user.user_id }));

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  return (
    <Grid container my={2}>
      {loading ? (
        <LoadingDashboard />
      ) : (
        <>
          <Grid container spacing={2} alignItems="center" mb={2}>
            <Grid item xs={8}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange} 
                  aria-label="user request tabs"
                  textColor="primary"
                  indicatorColor="primary"
                >
                  <Tab label="USER REQUESTS" data-testid="pending" id="pending" />
                  <Tab label="ACTIVE MEMBERS" data-testid="fulfilled" id="fullfilled" />
                </Tabs>
              </Box>
            </Grid>
            <Grid item xs={4} container justifyContent="flex-end">
              <TextField
                variant="outlined"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search..."
                label="Search"
                size="small"
                sx={{ width: "230px" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="medium" edge="end">
                        <Search />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          
          {rows.length === 0 ? (
            <Grid container direction="column" alignItems="center">
              <img
                src={nopending_img}
                alt="No pending"
                width="300px"
                height="300px"
                style={{ margin: "50px auto" }}
              />
              <Typography color="primary" id="noreq">
                Nice work!! No {tabValue === 0 ? "Pending" : "Active"} requests
              </Typography>
            </Grid>
          ) : (
            <Box sx={{ height: 400, width: '100%', mb: 2 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: rowsPerPage },
                  },
                }}
                pageSizeOptions={[5, 10, 15, 20]}
                onPaginationModelChange={(model) => {
                  setPage(model.page);
                  setRowsPerPage(model.pageSize);
                }}
                disableRowSelectionOnClick
                sx={{
                  '& .MuiDataGrid-cell:focus': { outline: 'none' },
                  '& .MuiDataGrid-columnHeader:focus': { outline: 'none' }
                }}
              />
            </Box>
          )}
        </>
      )}
    </Grid>
  );
};

export default Provision;